'use client';

import ContactListsIconSvg from '@/assets/svgs/main/contact-lists-fill.svg';
import {
  ColumnData,
  ContactListType,
  FieldNameOptions,
  MapContactListFormData,
  MappingData,
  UploadContactListFormData,
  UploadContactListResponse,
} from '@/containers/ContactLists/types';
import { Modals } from '@/containers/Modals/all-modals';
import useUploadContactListValidationSchema from '@/containers/Modals/P2PUploadModal/validators';
import {
  selectAccountIdentities,
  selectPrimaryRootAccount,
} from '@/lib/redux/app.selectors';
import {
  ContactListTypeMapping,
  mappingFields,
  optionalFeaturesList,
} from '@/shared/utils/contact-list';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalBody from '../ModalBody';
import ModalContainer from '../ModalContainer';
import ModalFooter from '../ModalFooter';
import ModalHeader from '../ModalHeader';
import { ModalProps, P2PUploadParams } from '../types';
import useModals from '../useModals';

import { useMapContactList, useUploadContactList } from '@/containers/ContactLists/api';
import { setShouldRefreshList } from '@/containers/ContactLists/redux/contactLists.slice';
import {
  getColumnsFromData,
  getSuggestedFirstNameColumn,
  getSuggestedPhoneNumberColumn,
} from '@/containers/ContactLists/utils/csvColumnMapping';
import { useToast } from '@/containers/ToastMessage';
import { useAppDispatch } from '@/lib/redux/store';
import useShouldValidateIdentity from '@/shared/hooks/useShouldValidateIdentity';
import P2PUploadStep1UploadFile from './P2PUploadStep1UploadFile';
import P2PUploadStep2SuggestPhone from './P2PUploadStep2SuggestPhone';
import P2PUploadStep3SuggestFirstName from './P2PUploadStep3SuggestFirstName';
import P2PUploadStep4Mapping from './P2PUploadStep4Mapping';

interface P2PUploadModalProps extends ModalProps {
  className?: string;
  params?: P2PUploadParams;
}
export interface UploadContactListFormValues {
  file?: File;
  accountNumber?: string;
  identity_id?: string;
  listName: string;
  listType: ContactListType;
  split_states: boolean; // Split States
  suppress_cell_phones: boolean; // Suppress Cell
  use_nat_dnc: boolean; // NDNC Scrub
  uses_rateplan?: boolean; // Uses Spotlight
  // use_state_dnc: boolean; // Scrub Against Florida DNC
}

const modalName = Modals.P2PUploadModal;
const P2PUploadModal = ({
  className,
  params,
  variant = 'default',
}: P2PUploadModalProps) => {
  const dispatch = useAppDispatch();
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const { showInfo } = useToast();
  const { isModalOpen, onClose } = useModals({
    modalName,
  });

  // Modal Specific details
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rootAccount = useSelector(selectPrimaryRootAccount);
  const accountIdentities = useSelector(selectAccountIdentities);
  const shouldValidateIdentity = useShouldValidateIdentity();
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [columnsData, setColumnsData] = useState<ColumnData[]>();
  // Step 2
  const [isPhoneMappingComplete, setIsPhoneMappingComplete] = useState(false);
  const [phoneMappingError, setPhoneMappingError] = useState<string>();
  const [suggestedPhoneNumberColumn, setSuggestedPhoneNumberColumn] =
    useState<ColumnData>();
  const [selectedPhoneNumberColumn, setSelectedPhoneNumberColumn] =
    useState<ColumnData>();

  const columnsOtherThanSuggestedPhoneNumberColumn = useMemo(() => {
    if (suggestedPhoneNumberColumn) {
      return columnsData?.filter(
        (c) => c.columnNumber != suggestedPhoneNumberColumn.columnNumber
      );
    }
    return columnsData;
  }, [suggestedPhoneNumberColumn, columnsData]);

  // Step 3
  const [isFirstNameMappingComplete, setIsFirstNameMappingComplete] = useState(false);
  const [suggestedFirstNameColumn, setSuggestedFirstNameColumn] = useState<ColumnData>();
  const [selectedFirstNameColumn, setSelectedFirstNameColumn] = useState<ColumnData>();
  const columnsOtherThanSuggestedFirstNameColumn = useMemo(() => {
    if (suggestedFirstNameColumn) {
      return columnsData?.filter((c) => {
        if (
          c.columnNumber != suggestedFirstNameColumn.columnNumber &&
          (selectedPhoneNumberColumn
            ? c.columnNumber != selectedPhoneNumberColumn.columnNumber
            : true)
        ) {
          return true;
        }
        return false;
      });
    }
    return columnsData?.filter((c) => {
      if (
        selectedPhoneNumberColumn
          ? c.columnNumber != selectedPhoneNumberColumn.columnNumber
          : true
      ) {
        return true;
      }
      return false;
    });
  }, [suggestedFirstNameColumn, selectedPhoneNumberColumn, columnsData]);

  // Step 4
  const [showMappingDialogForField, setShowMappingDialogForField] =
    useState<FieldNameOptions>();
  const blurClassNames = showMappingDialogForField
    ? 'pointer-events-none blur-[2px] transition-all duration-300'
    : '';
  const [mappingData, setMappingData] = useState<MappingData>({});
  const columnsAvailableForMapping = useMemo(() => {
    const unavailableColumnNumbers: string[] = Object.values(mappingData).map(String);
    return columnsData?.filter((c) => {
      return !unavailableColumnNumbers.includes(`${c.columnNumber}`);
    });
  }, [columnsData, mappingData]);

  const fieldsPendingMapping = useMemo(
    () =>
      mappingFields.filter((f) => {
        return !(Object.keys(mappingData) as FieldNameOptions[]).find(
          (fieldName) => fieldName === f.name && !!mappingData[fieldName]
        );
      }),
    [mappingData]
  );

  // Functionality
  const [fileName, setFileName] = useState<UploadContactListResponse['filename']>();

  const {
    data: uploadContactListData,
    error: uploadContactListError,
    isPending: isUploadContactListLoading,
    mutate: uploadContactList,
    reset: resetUploadContactList,
  } = useUploadContactList();
  const {
    data: mapContactListData,
    error: mapContactListError,
    isPending: isMapContactListLoading,
    mutate: mapContactList,
    reset: resetMapContactList,
  } = useMapContactList();

  const { UploadContactListValidationSchema, validFileExtensions } =
    useUploadContactListValidationSchema(shouldValidateIdentity);

  const formik = useFormik<UploadContactListFormValues>({
    validateOnBlur: true,
    initialValues: {
      file: undefined,
      accountNumber: rootAccount,
      identity_id: params?.identityId || '',
      listName: '',
      listType: params?.type || 'p2p-sms',
      split_states: false,
      use_nat_dnc: true,
      // use_state_dnc: false,
      suppress_cell_phones: false,
      uses_rateplan: false,
    },
    validationSchema: UploadContactListValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      if (isStep1) {
        setSubmitting(true);
        formik.validateForm();
        try {
          resetUploadContactList();
          setSubmitting(true);
          const formData: UploadContactListFormData = new FormData();
          formData.append('file', values.file as File);
          formData.append('account', values.accountNumber as string);
          formData.append('identity_id', values.identity_id as string);
          uploadContactList(formData);
        } catch (e) {
          setSubmitting(false);
          console.error(e);
        }
      } else if (isStep4) {
        setSubmitting(true);
        try {
          resetMapContactList();
          setSubmitting(true);
          const transformListMap: Record<string, number> = {};
          (Object.keys(mappingData) as FieldNameOptions[]).forEach((key) => {
            const raw = mappingData[key];
            if (raw === undefined || raw === null || raw === '') return;
            const n = parseInt(`${raw}`, 10);
            if (Number.isNaN(n)) return;
            transformListMap[key] = n;
          });
          const formData: MapContactListFormData = new FormData();
          formData.append('count', 1);
          formData.append('filename', `${fileName}`);
          formData.append('identity_id', values.identity_id as string);
          formData.append('list_name', values.listName);
          formData.append('split_states', values.split_states ? 1 : 0);
          formData.append('use_nat_dnc', values.use_nat_dnc ? 1 : 0);
          formData.append('uses_rateplan', values.uses_rateplan ? 1 : 0);
          formData.append('account', values.accountNumber as string);
          formData.append('list_map', JSON.stringify(transformListMap));
          formData.append('ver', '1.3');
          formData.append('opt_in', true);
          if (listType === 'p2p-sms') {
            formData.append('suppress_cell_phones', ContactListTypeMapping.P2PSms);
          } else if (listType === 'suppression') {
            formData.append(
              'suppress_cell_phones',
              ContactListTypeMapping.SuppressionList
            );
          } else if (listType === 'test') {
            formData.append('suppress_cell_phones', ContactListTypeMapping.Test);
          }
          mapContactList(formData);
        } catch (e) {
          console.error(e);
          setSubmitting(false);
        }
      }
    },
  });

  // console.log('val:', formik.values);
  // console.log('p2p upload mnodal params?.identityId:', params?.identityId);

  const isLoading = formik.isSubmitting;
  const listType = formik?.values?.listType as ContactListType;
  const optionalFeatures = useMemo(
    () => optionalFeaturesList[listType as ContactListType] ?? [],
    [listType]
  );

  const isFirstNameSuggestionAvailable =
    !!suggestedFirstNameColumn || !!columnsOtherThanSuggestedFirstNameColumn?.length;

  const isStep1 = !isFileUploaded;
  const isStep2 =
    isFileUploaded &&
    (listType === 'p2p-sms' || listType === 'suppression') &&
    !isPhoneMappingComplete;
  const isStep3 =
    isFileUploaded &&
    (listType === 'p2p-sms' || listType === 'suppression') &&
    isPhoneMappingComplete &&
    !isFirstNameMappingComplete &&
    isFirstNameSuggestionAvailable;
  const isStep4 =
    isFileUploaded &&
    (listType === 'p2p-sms' || listType === 'suppression') &&
    isPhoneMappingComplete &&
    (isFirstNameMappingComplete || !isFirstNameSuggestionAvailable);

  const progressPercentage = useMemo(() => {
    if (isStep2) {
      return 25;
    }
    if (isStep3) {
      return 50;
    }
    if (isStep4) {
      return 75;
    }
  }, [isStep2, isStep3, isStep4]);
  const shouldShowSkipButton = !!isStep3;

  const onBack = async () => {
    // step 2
    if (isStep2) {
      setMappingData({});
      setIsFileUploaded(false);
      setColumnsData(undefined);
      setFileName(undefined);
      formik.setFieldValue('file', '');
      formik.setFieldValue('listName', '');
      Object.keys(formik.touched)
        .filter(Boolean)
        .forEach((f) => formik.setFieldTouched(f, false));
    } else if (isStep3) {
      setIsPhoneMappingComplete(false);
    } else if (isStep4) {
      // No suggested names available, go back skipping the FirstName step
      if (!isFirstNameSuggestionAvailable) {
        setIsPhoneMappingComplete(false);
      }
      setIsFirstNameMappingComplete(false);
      if (!selectedFirstNameColumn) {
        setSelectedFirstNameColumn(suggestedFirstNameColumn);
      }
    }

    modalBodyRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const onSubmit = () => {
    if (isStep1) {
      formik.submitForm();
    } else if (isStep2) {
      if (selectedPhoneNumberColumn) {
        setIsPhoneMappingComplete(true);
        setPhoneMappingError('');
      } else {
        setPhoneMappingError('Please ensure to map a phone number column');
      }
    } else if (isStep3) {
      setIsFirstNameMappingComplete(true);
    } else if (isStep4) {
      formik.submitForm();
    }

    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const onSkip = useCallback(() => {
    if (isStep3) {
      setSelectedFirstNameColumn(undefined);
      setIsFirstNameMappingComplete(true);
    }
  }, [isStep3]);

  // Single Identity available, auto select it
  useEffect(() => {
    if (!shouldValidateIdentity && accountIdentities.length === 1) {
      formik.setFieldValue('identity_id', accountIdentities[0]?.identity_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountIdentities, shouldValidateIdentity]);

  // Set mapping data
  useEffect(() => {
    setMappingData((prev) => ({
      ...prev,
      lead_phone: selectedPhoneNumberColumn
        ? `${selectedPhoneNumberColumn.columnNumber}`
        : undefined,
      first_name: selectedFirstNameColumn
        ? `${selectedFirstNameColumn.columnNumber}`
        : undefined,
    }));
  }, [selectedFirstNameColumn, selectedPhoneNumberColumn]);

  // Sync account number with selected identity
  useEffect(() => {
    if (formik.values.identity_id) {
      // Per usage requirements, we need to use the Identity ID as the account context
      formik.setFieldValue('accountNumber', formik.values.identity_id);
    } else {
      // Fallback to root account if no identity selected
      if (rootAccount) {
        formik.setFieldValue('accountNumber', rootAccount);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.identity_id, rootAccount, formik.setFieldValue]);

  // Scroll to bottom once mapping is done for a custom field
  useEffect(() => {
    if (!showMappingDialogForField && modalBodyRef.current && isStep4) {
      modalBodyRef.current.scrollBy({
        top: 1000,
        behavior: 'smooth',
      });
    }
  }, [showMappingDialogForField, isStep4]);

  // File uploaded, provide suggestions
  useEffect(() => {
    if (
      !isUploadContactListLoading &&
      (!!uploadContactListData || !!uploadContactListError)
    ) {
      formik?.setSubmitting(false);

      // TODO: Handle error pending
      if (!isUploadContactListLoading && !!uploadContactListData) {
        const rawColumnData = getColumnsFromData(uploadContactListData.data.sample);
        const organizedColumnData: typeof columnsData = [];
        rawColumnData.forEach((c, idx) => {
          organizedColumnData.push({
            columnNumber: idx + 1,
            data: c,
          });
        });
        setColumnsData(organizedColumnData);
        setFileName(uploadContactListData.data.filename);

        // Phone number suggestion
        const phoneColumnSuggestion = getSuggestedPhoneNumberColumn(organizedColumnData);
        setSuggestedPhoneNumberColumn(phoneColumnSuggestion);
        setSelectedPhoneNumberColumn(phoneColumnSuggestion);
        // First name suggestion
        const firstNameColumnSuggestion =
          getSuggestedFirstNameColumn(organizedColumnData);
        setSuggestedFirstNameColumn(firstNameColumnSuggestion);
        setSelectedFirstNameColumn(firstNameColumnSuggestion);

        setIsFileUploaded(true);
        modalBodyRef.current?.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploadContactListLoading, uploadContactListError, uploadContactListData]);

  // List Mapping complete, show success / error
  useEffect(() => {
    if (!isMapContactListLoading && (mapContactListData || mapContactListError)) {
      formik.setSubmitting(false);
      if (mapContactListData) {
        dispatch(setShouldRefreshList(true));
        showInfo(
          'Your contact list upload is being submitted. Once finished, you will receive a notification.'
        );
        onClose();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapContactListLoading, mapContactListData, mapContactListError]);

  // Scroll below as these are dependent steps
  useEffect(() => {
    if (modalBodyRef.current && formik?.values?.listType && formik?.touched?.listType) {
      modalBodyRef.current.scrollBy({ top: 1000, behavior: 'smooth' });
    }
  }, [formik?.touched, formik?.values?.listType]);

  const unmount = () => {
    if (formik) {
      formik?.resetForm();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => unmount, []);

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />

      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        variant={variant}
        onClose={onClose}>
        <form onSubmit={formik.handleSubmit} className="h-full">
          <div className="flex h-full flex-col">
            {/* Stick to top */}
            <ModalHeader
              modalName={modalName}
              className={blurClassNames}
              icon={<ContactListsIconSvg className="h-5 w-5" />}
              title="Upload Contact List"
              closeAction="state"
            />

            {/* Take up remaining space and enable inside-scrolling */}
            <ModalBody ref={modalBodyRef}>
              {/* 1. Workflow for Uploading contact list CSV */}
              {isStep1 && (
                <P2PUploadStep1UploadFile
                  params={params}
                  formik={formik}
                  isLoading={isLoading}
                  validFileExtensions={validFileExtensions}
                  fileInputRef={fileInputRef}
                  shouldValidateIdentity={shouldValidateIdentity}
                  optionalFeatures={optionalFeatures}
                />
              )}

              {/* 2. Workflow for Phone Number column Suggestion */}
              {isStep2 && (
                <P2PUploadStep2SuggestPhone
                  phoneMappingError={phoneMappingError}
                  suggestedPhoneNumberColumn={suggestedPhoneNumberColumn}
                  selectedPhoneNumberColumn={selectedPhoneNumberColumn}
                  columnsOtherThanSuggestedPhoneNumberColumn={
                    columnsOtherThanSuggestedPhoneNumberColumn
                  }
                  setSelectedPhoneNumberColumn={setSelectedPhoneNumberColumn}
                />
              )}

              {/* 3. Workflow for First Name column Suggestion */}
              {isStep3 && (
                <P2PUploadStep3SuggestFirstName
                  suggestedFirstNameColumn={suggestedFirstNameColumn}
                  selectedFirstNameColumn={selectedFirstNameColumn}
                  columnsOtherThanSuggestedFirstNameColumn={
                    columnsOtherThanSuggestedFirstNameColumn
                  }
                  setSelectedFirstNameColumn={setSelectedFirstNameColumn}
                />
              )}

              {/* 4. Reviewing and managing all mappings (add / edit / delete) */}
              {isStep4 && (
                <P2PUploadStep4Mapping
                  mappingData={mappingData}
                  blurClassNames={blurClassNames}
                  columnsData={columnsData}
                  fieldsPendingMapping={fieldsPendingMapping}
                  showMappingDialogForField={showMappingDialogForField}
                  columnsAvailableForMapping={columnsAvailableForMapping}
                  setShowMappingDialogForField={setShowMappingDialogForField}
                  setMappingData={setMappingData}
                />
              )}
            </ModalBody>

            <ModalFooter
              progressPercentage={progressPercentage}
              className={blurClassNames}>
              {isStep1 ? (
                <div className="flex-1" />
              ) : (
                <button
                  type="button"
                  onClick={onBack}
                  className="btn btn-outline border-gray-200 dark:border-gray-600 dark:text-gray-300">
                  Back
                </button>
              )}
              <div className="flex gap-3">
                {shouldShowSkipButton && (
                  <button
                    disabled={isLoading}
                    type="button"
                    onClick={onSkip}
                    className="btn btn-outline bg-base-100 border-gray-100">
                    Skip
                  </button>
                )}
                <button
                  disabled={isLoading}
                  type="button"
                  onClick={onSubmit}
                  className="btn btn-primary">
                  {isLoading && isStep1 && (
                    <>
                      <span className="loading loading-spinner"></span>
                      <span>Uploading list...</span>
                    </>
                  )}
                  {isLoading && isStep4 && (
                    <>
                      <span className="loading loading-spinner"></span>
                      <span>Submitting...</span>
                    </>
                  )}
                  {!isLoading && !isStep4 && 'Next'}
                  {!isLoading && isStep4 && 'Submit'}
                </button>
              </div>
            </ModalFooter>
          </div>
        </form>
      </ModalContainer>
    </div>
  );
};

export default P2PUploadModal;
